// packages/utils/src/storage/database/schema.ts
import { Dexie } from 'dexie';
/**
 * Defines the database name template.
 * The final database name will be `ReadSavorDB_{userId}`.
 */
export const DATABASE_NAME_TEMPLATE = 'ReadSavorDB_{userId}';
/**
 * Defines the database version.
 * Increment this version if you change table schemas or indexes.
 */
export const DATABASE_VERSION = 10;
/**
 * Represents the structure of the ReadSavor database for a single user.
 * An instance of this class is created for each user.
 */
export class ReadSavorDatabase extends Dexie {
    /**
     * @param userId The ID of the user for whom this database is created.
     */
    constructor(userId) {
        const dbName = DATABASE_NAME_TEMPLATE.replace('{userId}', userId);
        super(dbName);
        this.defineSchema();
    }
    defineSchema() {
        this.version(DATABASE_VERSION).stores({
            textDocuments: '++id, contentType, timestamp, size',
            textParagraphs: '++id, [textId+index], [textId+paragraphId]',
            textSelectionTranslations: '++orderId, &id, [documentIdentity.id+documentIdentity.type], [documentIdentity.id+location.paragraphId]',
            pdfDocuments: '++id, contentType, timestamp, size, filename, title',
            pdfPageInfos: '++id, [documentId+pageIndex]',
            pdfParagraphs: '++id, documentId, &[documentId+index], paragraphId, [documentId+pageNumber]',
        });
        this.textDocuments = this.table('textDocuments');
        this.textParagraphs = this.table('textParagraphs');
        this.textSelectionTranslations = this.table('textSelectionTranslations');
        this.pdfDocuments = this.table('pdfDocuments');
        this.pdfPageInfos = this.table('pdfPageInfos');
        this.pdfParagraphs = this.table('pdfParagraphs');
    }
    async connect() {
        try {
            if (!this.isOpen()) {
                await this.open();
            }
        }
        catch (error) {
            console.error(`❌ Failed to open Dexie database "${this.name}":`, error);
            throw error;
        }
    }
}
// --- HMR-safe Singleton Pattern for Multiple DB Instances ---
const SKEY_TEMPLATE = 'ReadSavor.Dexie.Database.instance_{userId}';
const globalWithDb = globalThis;
/**
 * Gets a singleton instance of the database for a specific user.
 * Manages multiple instances in a global, HMR-safe manner.
 * @param userId The ID of the user.
 * @returns The Dexie database instance for that user.
 */
export const getDb = (userId) => {
    if (!userId) {
        throw new Error('User ID is required to get a database instance.');
    }
    const skey = SKEY_TEMPLATE.replace('{userId}', userId);
    if (!globalWithDb[skey]) {
        globalWithDb[skey] = new ReadSavorDatabase(userId);
    }
    return globalWithDb[skey];
};
//# sourceMappingURL=schema.js.map